import { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import { useQuery } from '@tanstack/react-query'

import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Heading from 'LuxKit/Typography/Heading'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import { useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'
import ModalContext from 'contexts/ModalContext'
import VoucherDetailsModal from 'components/Pages/VouchersPage/VoucherDetailsModal'
import { getAllVouchers } from 'api/promo'
import VoucherCarouselCard from './VoucherCarouselCard'
import { isVoucherHidden } from 'lib/voucher/isVoucherHidden'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'

const SectionContainer = styled(LayoutContainer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

function MyVouchersCarousel() {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const isLoggedIn = !!memberId
  const isEnabled = config.ENABLE_MY_VOUCHERS_HOMEPAGE_CAROUSEL

  const { data: vouchers = [] } = useQuery({
    queryKey: ['my-vouchers-active', region, memberId],
    queryFn: () => getAllVouchers({
      region,
      status: 'active',
    }),
    enabled: isEnabled && isLoggedIn,
    staleTime: 5 * 60 * 1000,
  })
  const visibleVouchers = useMemo(() => (
    vouchers.filter(voucher => !isVoucherHidden(voucher))
  ), [vouchers])

  const handleDetailsClick = useCallback((voucher: App.PublicVoucher) => {
    if (!voucher.voucherDescription) return
    const title = voucher.voucherTitle || voucher.voucherName
    showModal(<VoucherDetailsModal title={title} voucher={voucher} />)
  }, [showModal])

  const getExpiryText = useCallback((expiresAt: string | undefined): string | undefined => {
    if (!expiresAt) return undefined
    return intl.formatMessage(
      {
        id: 'xo490XgN_oxAUFu5Tggh',
        defaultMessage: 'Expires {expiryDate}',
        description: 'Expiry date text shown on voucher cards in the available rewards section',
      },
      {
        expiryDate: intlDateFormatter(new Date(expiresAt), INTL_DMY_CASUAL_SHORT_FORMAT, intl.locale),
      },
    )
  }, [intl])

  if (!isEnabled || !isLoggedIn || visibleVouchers.length === 0) {
    return null
  }

  return (
    <SectionContainer>
      <Group direction="vertical" gap={20}>
        <Heading variant="heading2" as="h2">
          <FormattedMessage
            id="Y7O-zKq1Z1HfftNg3fm4"
            defaultMessage="Your <italic>rewards</italic>"
            description="Section heading for the logged-in user's assigned reward vouchers on the homepage"
            values={{
              italic: (chunks) => <em key="your-rewards-italic">{chunks}</em>,
            }}
          />
        </Heading>

        <CSSBreakpoint max="mobile">
          <HorizontalContainerWithFadedOverflow gap={12}>
            {visibleVouchers.map((voucher) => (
              <VoucherCarouselCard
                key={voucher.voucherId}
                variant="mobile"
                voucher={voucher}
                expiryText={getExpiryText(voucher.expiresAt)}
                rightSlot={<LineAngleRightIcon size="L" colour="light-foreground-alt" />}
                onDetailsClick={voucher.voucherDescription ? () => handleDetailsClick(voucher) : undefined}
              />
            ))}
          </HorizontalContainerWithFadedOverflow>
        </CSSBreakpoint>

        <CSSBreakpoint min="tablet">
          <HorizontalContainerWithFadedOverflow gap={20}>
            {visibleVouchers.map((voucher) => (
              <VoucherCarouselCard
                key={voucher.voucherId}
                variant="desktop"
                voucher={voucher}
                expiryText={getExpiryText(voucher.expiresAt)}
                rightSlot={<LineAngleRightIcon size="L" colour="light-foreground-alt" />}
                onDetailsClick={voucher.voucherDescription ? () => handleDetailsClick(voucher) : undefined}
              />
            ))}
          </HorizontalContainerWithFadedOverflow>
        </CSSBreakpoint>
      </Group>
    </SectionContainer>
  )
}

export default MyVouchersCarousel
